// [목적] 폴리오 PDF 내보내기 버튼 — 요청/폴링 진행 상태 표시 + 완료 시 자동 다운로드 + 실패 재시도
// [이유] FlowLine A2(49df7920): useFolioExport 상태 머신을 시각화. FolioShareButton 과 동일하게
//        헤드리스(sx 로 시각 스타일은 호출부 주입) — 데스크톱/모바일 헤더 액션 바에 그대로 삽입 가능.
//        2026-09-18 보안 조치(O-4): <a href> 직접 navigate 는 익명 세션 헤더 미첨부로 익명 다운로드가
//        항상 404, AT 만료 시 refresh 미동작이었다 — downloadFolioExport() 의 Blob 을 받아 다운로드하도록 변경.
// [사이드 이팩트] status='done' 도달 시 downloadFolioExport() 로 Blob fetch → URL.createObjectURL 로 감싼
//                <a download> 프로그래매틱 클릭 후 즉시 revokeObjectURL. 410(만료) 응답은 "다시 내보내기" 안내로 분기.
// [수정 시 고려사항] busy 상태에서는 버튼 disabled — 중복 요청 방지. 실패/타임아웃/다운로드실패/만료 후 재클릭 시
//                   reset() + downloadError 초기화 후 재요청.

import { useEffect, useState } from 'react';
import { ButtonBase, CircularProgress, type SxProps, type Theme } from '@mui/material';

import { downloadFolioExport } from '../../api/folio-export.client.js';
import { ApiError } from '../../api/http.js';
import { useFolioExport } from '../../hooks/useFolioExport.js';
import { useToast } from '../feedback/Toast.js';

export interface FolioExportButtonProps {
  folioId: string;
  sx?: SxProps<Theme>;
}

/**
 * [목적] PDF 내보내기 트리거 + 진행 상태 라벨
 * [이유] A2 — "진행 상태 표시·실패 재시도" 요건.
 * [사이드 이팩트] useFolioExport 의 status/timedOut/startError 변화를 useToast 로 안내.
 * [수정 시 고려사항] downloadTriggered 로 done 상태당 다운로드 1회만 트리거(폴링 재조회로 인한 중복 방지).
 */
export function FolioExportButton({ folioId, sx }: FolioExportButtonProps): JSX.Element {
  const { show } = useToast();
  const { start, isStarting, exportId, status, timedOut, startError, reset } =
    useFolioExport(folioId);
  const [downloadTriggered, setDownloadTriggered] = useState(false);
  // [목적] 다운로드(Blob fetch) 자체의 실패 상태 — 생성 실패(status='failed')와 구분해 410(만료) 시
  //        다른 안내 문구를 노출한다.
  // [이유] O-4 조치: <a href> navigate 는 실패를 감지할 수 없었으나 fetch 기반은 실패를 코드로 구분 가능.
  const [downloadError, setDownloadError] = useState<'failed' | 'expired' | null>(null);

  const isFailed = status?.status === 'failed' || timedOut || startError || downloadError !== null;
  const busy = isStarting || (exportId != null && !isFailed && status?.status !== 'done');

  useEffect(() => {
    if (status?.status === 'done' && exportId && !downloadTriggered) {
      setDownloadTriggered(true);
      // [목적] Blob 다운로드 — downloadFolioExport() 가 anon 헤더 첨부 + 401 refresh 재시도까지 처리
      // [이유] O-4: <a href> 직접 navigate 는 익명 세션 헤더가 브라우저 네비게이션에 실리지 않아
      //        익명 다운로드가 항상 404, AT 만료 시 refresh 없이 실패하던 문제를 해결.
      // [사이드 이팩트] Blob URL 은 다운로드 트리거 직후 즉시 revokeObjectURL 로 해제(메모리 누수 방지).
      void (async () => {
        try {
          const { blob, filename } = await downloadFolioExport(folioId, exportId);
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(blobUrl);
          show('PDF 다운로드를 시작했어요.', 'success');
        } catch (err) {
          if (err instanceof ApiError && err.status === 410) {
            setDownloadError('expired');
            show('다운로드 유효 기간이 지났어요. 다시 내보내기 해주세요.', 'warning');
          } else {
            setDownloadError('failed');
            show('PDF 다운로드에 실패했어요. 다시 시도해 주세요.', 'error');
          }
        }
      })();
    }
  }, [status?.status, exportId, downloadTriggered, folioId, show]);

  useEffect(() => {
    if (status?.status === 'failed') show('PDF 생성에 실패했어요. 다시 시도해 주세요.', 'error');
  }, [status?.status, show]);

  useEffect(() => {
    if (timedOut) show('PDF 생성이 지연되고 있어요. 잠시 후 다시 시도해 주세요.', 'warning');
  }, [timedOut, show]);

  useEffect(() => {
    if (startError) show('PDF 내보내기 요청에 실패했어요.', 'error');
  }, [startError, show]);

  function handleClick(): void {
    if (busy) return;
    setDownloadTriggered(false);
    setDownloadError(null);
    if (isFailed) reset();
    start();
  }

  const label = busy
    ? '생성 중…'
    : isFailed
      ? downloadError === 'expired'
        ? '다시 내보내기'
        : 'PDF 재시도'
      : 'PDF';

  return (
    <ButtonBase
      onClick={handleClick}
      disabled={busy}
      aria-label={busy ? 'PDF 생성 중' : 'PDF 내보내기'}
      aria-live="polite"
      sx={sx}
    >
      {busy && <CircularProgress size={12} sx={{ color: 'inherit', mr: '4px' }} />}
      {label}
    </ButtonBase>
  );
}
